import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import { EventClickArg, EventContentArg, EventInput } from "@fullcalendar/core";
import PulseLoader from "react-spinners/PulseLoader";
import { getAllEventsByVenue } from "../../api";
import { EventType } from "../../types/Events";
import { getVenueOrganizerData } from "../../utils/auth";
import "./../../assets/css/calendar-style.css";

const renderEventContent = (info: EventContentArg) => {
  const event = info.event.extendedProps as EventType;
  const image = event.thumbImg || null;

  return (
    <div className="ex-fc-event-chip">
      {image && (
        <img src={image} alt="" className="ex-fc-event-thumb" />
      )}
      <span className="ex-fc-event-title">{info.event.title}</span>
    </div>
  );
};

const EventSchedule: React.FC = () => {
  const [events, setEvents] = useState<EventInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);

  const navigate = useNavigate();

  const venueOrganizerData = useMemo(() => {
    return getVenueOrganizerData();
  }, []);

  useEffect(() => {
    const venueId = venueOrganizerData?.userId;
    if (!venueId) return;

    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await getAllEventsByVenue(venueId);
        const res = response as any[];

        const resolveLocation = (e: any): string => {
          if (e.eventLocation || e.event_location) {
            return e.eventLocation || e.event_location;
          }
          // Venue create stores address in locationData JSON, not event_location
          let loc = e.locationData;
          if (typeof loc === "string") {
            try {
              loc = JSON.parse(loc);
            } catch {
              loc = null;
            }
          }
          const first = Array.isArray(loc) ? loc[0] : loc;
          if (!first || typeof first !== "object") return "";
          // Prefer real address fields — do not use .name (often venue/organizer name)
          const parts = [first.formattedAddress, first.addressLine1, first.city, first.state]
            .map((p) => (typeof p === "string" ? p.trim() : ""))
            .filter(Boolean);
          return parts[0] || "";
        };

        const mapped: EventInput[] = res
          .filter(e => e.eventDate || e.event_date)
          .map(e => {
            const eventName = e.eventName || e.event_name || "";
            const organizerName = e.organizerName || e.organizer_name || "";
            return {
            id: String(e.eventId || e.event_id),
            // Calendar chip must show event title, never organizer name
            title: eventName || "Untitled event",
            start: e.eventDate || e.event_date,
            allDay: true,
            extendedProps: {
              ...e,
              eventId: e.eventId || (e.event_id ? Number(e.event_id) : undefined),
              eventName,
              organizerName,
              eventDate: e.eventDate || e.event_date || "",
              eventTime: e.eventTime || e.event_time || "",
              endTime: e.endTime || e.event_end_date || "",
              eventDescription: e.eventDescription || e.event_description || "",
              eventLocation: resolveLocation(e),
              thumbImg: e.thumbImg || e.event_image || "",
              status: e.status || e.event_status || undefined,
            },
          };
          });

        setEvents(mapped);
      } catch (err) {
        console.error("Failed to load events", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [venueOrganizerData]);

  const handleDateClick = (info: DateClickArg) => {
    setShowEventModal(false);
    setSelectedEvent(null);
    setSelectedDate(info.dateStr);
    setShowModal(true);
  };

  // Click an event chip → show details (not create)
  const handleEventClick = (info: EventClickArg) => {
    info.jsEvent.preventDefault();
    info.jsEvent.stopPropagation();
    setShowModal(false);
    setSelectedEvent(info.event.extendedProps as EventType);
    setShowEventModal(true);
  };

  const formatDisplayDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="ex-schedule-page">
        <div className="ex-page-header">
          <span className="ex-page-eyebrow">Venue</span>
          <h1 className="ex-page-title">
            <i className="fa-solid fa-calendar-week me-2" aria-hidden="true"></i>
            Events Schedule
          </h1>
        </div>
        <div className="event-schedule-calendar-wrapper">
          <div className="event-schedule-loading">
            <PulseLoader color="#EE3F7C" size={12} />
            <span className="event-schedule-loading-text">Loading schedule…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ex-schedule-page">
      <div className="ex-page-header">
        <span className="ex-page-eyebrow">Venue</span>
        <h1 className="ex-page-title">
          <i className="fa-solid fa-calendar-week me-2" aria-hidden="true"></i>
          Events Schedule
        </h1>
        <p className="ex-page-sub">Click an event for details, or a day to list a new event</p>
      </div>

      <div className="event-schedule-calendar-wrapper">
      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        events={events}
        eventContent={renderEventContent}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        height="auto"
        dayMaxEvents={3}
        moreLinkClick="popover"
      />

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <h3 className="schedule-modal-date">
              {selectedDate &&
                new Date(selectedDate).toLocaleDateString("en-IN", {
                  weekday: "long",
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
            </h3>

            <div
              className="schedule-modal-card"
              onClick={() => {
                setShowModal(false);
                navigate("/create-venue-event", {
                  state: { date: selectedDate },
                });
              }}
            >
              <div className="schedule-modal-icon-wrapper">
                📅
              </div>

              <div className="schedule-modal-text-wrapper">
                <h5 className="schedule-modal-title">List Events</h5>
                <p className="schedule-modal-desc">
                  List a New Event
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEventModal && selectedEvent && (
        <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
          <div
            className="modal-container schedule-event-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="schedule-modal-date">Event details</h3>

            {selectedEvent.thumbImg ? (
              <img
                src={selectedEvent.thumbImg}
                alt=""
                className="schedule-event-detail-thumb"
              />
            ) : null}

            <ul className="schedule-event-detail-list">
              <li>
                <span>Event</span>
                <strong>{selectedEvent.eventName || "Untitled event"}</strong>
              </li>
              <li>
                <span>Organizer</span>
                <strong>
                  {(selectedEvent as any).organizerName ||
                    (selectedEvent as any).organizer_name ||
                    "—"}
                </strong>
              </li>
              <li>
                <span>Date</span>
                <strong>{formatDisplayDate(selectedEvent.eventDate)}</strong>
              </li>
              <li>
                <span>Time</span>
                <strong>{selectedEvent.eventTime || "—"}</strong>
              </li>
              <li>
                <span>Status</span>
                <strong>{selectedEvent.status || "—"}</strong>
              </li>
              <li>
                <span>Description</span>
                <strong>{selectedEvent.eventDescription || "—"}</strong>
              </li>
            </ul>

            <div className="schedule-event-detail-actions">
              <button
                type="button"
                className="schedule-event-detail-btn secondary"
                onClick={() => setShowEventModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="schedule-event-detail-btn primary"
                onClick={() => {
                  setShowEventModal(false);
                  navigate("/edit-venue-event", {
                    state: { event: selectedEvent },
                  });
                }}
              >
                Edit event
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default EventSchedule;
